/**
 * Keep the screen on while something long runs (Screen Wake Lock API).
 *
 * Shared by the whole web: the migration tool holds it while it hashes,
 * compresses and uploads, and every upload with a progress ring holds it while
 * the bytes go (`hooks/useUploads.ts`) — a phone that locks its screen freezes
 * the page and the upload dies with it. Moved here from the migration's
 * `lifecycle.ts`, which re-exports it.
 *
 * Pure: the browser APIs are injected (`WakeLockEnv`), Node-tested in
 * `tenant/frontend/services/migration/lifecycle.test.ts`.
 */

const NOOP = (): void => undefined;


export interface WakeLockSentinelLike {
    released?: boolean;
    release(): Promise<void>;
    addEventListener?(type: 'release', listener: () => void): void;
}

export interface WakeLockEnv {
    /** `navigator.wakeLock`. */
    wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinelLike> } | null;
    /** `document`: its visibility decides whether a lock can be held at all. */
    document?: {
        visibilityState?: string;
        addEventListener(type: 'visibilitychange', listener: () => void): void;
        removeEventListener(type: 'visibilitychange', listener: () => void): void;
    } | null;
}

export interface WakeLockState {
    /** The browser has the Screen Wake Lock API at all. */
    supported: boolean;
    /** Someone asked for it (a long operation is running). */
    wanted: boolean;
    /** The screen is being kept awake right now. */
    active: boolean;
    /** The last request was refused (battery saver, policy…). */
    failed: boolean;
}

export interface WakeLockController {
    /** Ask to keep the screen awake. Returns the matching release (idempotent);
     *  the lock is held while at least one hold is open. */
    hold(): () => void;
    getState(): WakeLockState;
    /** Called on every state change; returns the unsubscribe. */
    subscribe(listener: (state: WakeLockState) => void): () => void;
    /** Releases everything and stops listening. */
    dispose(): void;
}

export function createWakeLock(env: WakeLockEnv): WakeLockController {
    const api = env.wakeLock && typeof env.wakeLock.request === 'function' ? env.wakeLock : null;
    const doc = env.document ?? null;
    const listeners = new Set<(state: WakeLockState) => void>();
    let holds = 0;
    let sentinel: WakeLockSentinelLike | null = null;
    let requesting = false;
    let failed = false;
    let disposed = false;

    const state = (): WakeLockState => ({
        supported: api !== null,
        wanted: holds > 0,
        active: sentinel !== null && sentinel.released !== true,
        failed,
    });
    const emit = (): void => {
        const snapshot = state();
        for (const listener of listeners) listener(snapshot);
    };
    const visible = (): boolean => !doc || doc.visibilityState === undefined || doc.visibilityState === 'visible';

    const dropSentinel = (): void => {
        const current = sentinel;
        sentinel = null;
        if (current && current.released !== true) current.release().catch(NOOP);
    };

    const ensure = (): void => {
        if (!api || disposed || holds === 0 || requesting || !visible()) return;
        if (sentinel && sentinel.released !== true) return;
        requesting = true;
        api.request('screen').then(
            (lock) => {
                requesting = false;
                if (disposed || holds === 0) {
                    // Everyone let go while the request was in flight.
                    lock.release().catch(NOOP);
                    emit();
                    return;
                }
                sentinel = lock;
                failed = false;
                // The browser releases it by itself when the tab is hidden.
                lock.addEventListener?.('release', () => {
                    if (sentinel === lock) sentinel = null;
                    emit();
                });
                emit();
            },
            () => {
                requesting = false;
                failed = true;
                emit();
            },
        );
    };

    const onVisibility = (): void => {
        if (visible()) ensure();
    };
    if (api && doc) doc.addEventListener('visibilitychange', onVisibility);

    return {
        hold(): () => void {
            holds += 1;
            ensure();
            emit();
            let open = true;
            return () => {
                if (!open) return;
                open = false;
                holds = Math.max(0, holds - 1);
                if (holds === 0) dropSentinel();
                emit();
            };
        },
        getState: state,
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        dispose(): void {
            if (disposed) return;
            disposed = true;
            holds = 0;
            dropSentinel();
            if (api && doc) doc.removeEventListener('visibilitychange', onVisibility);
            listeners.clear();
        },
    };
}
